/**
 * "Act as" role for human UAT — a privilege DOWNGRADE, never a grant.
 *
 * Bob asked for a dropdown so testers can change role and actually run the
 * test cases we have written. The obvious build — grant the tester the role
 * the test names — cannot work: the backend passes any gate the moment
 * ORG_ADMIN is held, and our testers already hold it. Adding VIEWER changes
 * nothing.
 *
 * So the backend NARROWS the effective role set instead, and only ever
 * downwards (see `narrow_roles_for_act_as`). This module is the client half:
 * it remembers the chosen role and sends it as a header. The backend is the
 * authority — if this sends a role the user does not outrank, it is IGNORED
 * and their real roles stand. Nothing here can grant anything.
 *
 * sessionStorage, NOT localStorage, and the distinction matters:
 *   * per-TAB, so a tester can hold a VIEWER tab and an admin tab side by side
 *     — which is exactly how you compare what each role can see;
 *   * cleared when the tab closes, so an assumed role cannot quietly outlive
 *     the testing session and confuse someone tomorrow.
 */

const KEY = "ftl.actAsRole";

/** Ranks mirror the backend `_ROLE_RANK`. Kept in sync by a test. */
export const ROLE_RANK: Record<string, number> = {
  ORG_ADMIN: 100,
  PROGRAM_DIRECTOR: 60,
  PROJECT_OWNER: 50,
  ANALYST: 20,
  DEVELOPER: 20,
  QA_ENGINEER: 20,
  VIEWER: 10,
};

export const ROLE_LABELS: Record<string, string> = {
  ORG_ADMIN: "Org admin",
  PROGRAM_DIRECTOR: "Program director",
  PROJECT_OWNER: "Project owner",
  ANALYST: "Analyst",
  DEVELOPER: "Developer",
  QA_ENGINEER: "QA engineer",
  VIEWER: "Viewer (read-only)",
};

export function getActAsRole(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null; // private mode / storage disabled — act-as simply unavailable
  }
}

export function setActAsRole(role: string | null): void {
  try {
    if (role) sessionStorage.setItem(KEY, role);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable — the selector will read back null and show real role */
  }
}

/**
 * Which roles this user may assume.
 *
 * ONLY roles at or below their highest held rank. Offering a higher one would
 * render a control that silently does nothing, because the backend ignores it
 * — and a control that appears to work while changing nothing is the exact
 * failure this whole feature exists to let testers catch.
 */
export function assumableRoles(heldRoles: string[]): string[] {
  const held = Math.max(0, ...heldRoles.map((r) => ROLE_RANK[r] ?? 0));
  if (held <= 0) return [];
  return Object.keys(ROLE_RANK)
    .filter((r) => ROLE_RANK[r] <= held)
    .sort((a, b) => ROLE_RANK[b] - ROLE_RANK[a]);
}
